/**
 * Autoplay Test Service - Serviço para gerenciamento de testes A/B de autoplay
 *
 * Integra com as rotas do backend em /autoplay-test
 */

import { api } from './api';
import { AutoplayVariation, AutoplayABTestConfig, AutoplayMetrics } from '@/store/videoFeatures/types/autoplay-ab-test';
import type { TestPitchMetric } from '@/types/pitch';

export interface CreateAutoplayTestPayload {
  autoplayTestData: {
    autoplayTestTitle: string;
    userId: string;
    endDate?: string;
  };
  autoplayTestConfig: Array<{
    typesAutoplay: string; // ID do template (ex: 'auto-1', 'auto-2')
    textSuperior?: string;
    textInferior?: string;
    cor?: string; // background color
    corText?: string; // text color
    autoplayIconColor?: string;
    haveBorderWhite?: boolean;
    havePulse?: boolean;
    haveCustomImage?: boolean;
    customImage?: string;
    haveAutoplay: boolean;
  }>;
}

export interface UpdateAutoplayTestTitlePayload {
  autoplayTestTitle: string;
}

export interface UpdateAutoplayTestTrafficPayload {
  videosIds: Array<{
    id: string;
    trafego: number;
  }>;
}

export interface AutoplayTestResponse {
  id: string;
  id_autoplay_test?: string;
  originalVideoId: string;
  userId: string;
  title?: string;
  variations: AutoplayVariation[];
  trafficDistribution: Record<string, number>;
  status: 'active' | 'finished' | 'draft' | 'paused';
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AutoplayTestMetricsResponse {
  message: string;
  data: Array<{
    autoplayTest: {
      id_video: string;
      trafego: number;
      contador_trafego: number;
      nomeVideo: string;
      frame: string;
    };
    metric: {
      visualizacoes: Record<string, any>;
      totais: {
        visualizacoes: number;
        visualizacoesUnicas: number;
        plays: number;
        playsUnicos: number;
        clicks: number;
        taxareproducao: number;
        pitch: string;
        engajamento: number;
        somaRetencoes: number;
        somaEngajamento: number;
        duracaoVideo: number;
      };
      /** Pitches do vídeo ORIGINAL com a retenção desta variação em cada um. */
      pitches?: TestPitchMetric[];
      /** Vídeo onde o pitch é gravado (o original do teste). */
      pitchVideoId?: string | null;
      videoDuration?: number | null;
    };
    // Campos no nível raiz de cada item (não dentro de metric)
    retencao: Record<string, { count: number; percent: number | null }>;
    dispositivos: any[];
    estados: any[];
    navegadores: any[];
    paises: any[];
    videoOrigin: string;
    visualizacoes: Record<string, any>;
  }>;
}

export const autoplayTestService = {
  /**
   * POST /autoplay-test/:originalVideoId
   * Cria um novo teste A/B de autoplay
   */
  createAutoplayTest: async (
    originalVideoId: string,
    payload: CreateAutoplayTestPayload
  ): Promise<AutoplayTestResponse> => {
    return await api.post(`/autoplay-test/${originalVideoId}`, payload);
  },

  /**
   * PUT /autoplay-test/:autoplayTestId
   * Atualiza o título do teste
   */
  updateTestTitle: async (
    autoplayTestId: string,
    payload: UpdateAutoplayTestTitlePayload
  ): Promise<AutoplayTestResponse> => {
    return await api.put(`/autoplay-test/${autoplayTestId}`, payload);
  },

  /**
   * DELETE /autoplay-test/:autoplayTestId
   * Deleta um teste de autoplay
   */
  deleteAutoplayTest: async (autoplayTestId: string): Promise<void> => {
    return await api.delete(`/autoplay-test/${autoplayTestId}`);
  },

  /**
   * GET /autoplay-test/:autoplayTestId/videos
   * Busca os vídeos/variações de um teste
   */
  getAutoplayTestVideos: async (autoplayTestId: string): Promise<{
    test: AutoplayTestResponse;
    videos: any[];
  }> => {
    return await api.get(`/autoplay-test/${autoplayTestId}/videos`);
  },

  /**
   * GET /autoplay-test/:autoplayTestId/metrics
   * Busca métricas do teste A/B
   * @param autoplayTestId - ID do teste
   * @param inicio - Data inicial no formato YYYY-MM-DD
   * @param fim - Data final no formato YYYY-MM-DD
   */
  getAutoplayTestMetrics: async (
    autoplayTestId: string,
    inicio?: string,
    fim?: string
  ): Promise<AutoplayTestMetricsResponse> => {
    // Se não passar datas, usar últimos 30 dias
    const endDate = fim || new Date().toISOString().split('T')[0];
    const startDate = inicio || (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split('T')[0];
    })();

    return await api.get(`/autoplay-test/${autoplayTestId}/metrics?inicio=${startDate}&fim=${endDate}`);
  },

  /**
   * GET /autoplay-test/:userId
   * Busca todos os testes de autoplay do usuário
   */
  findUserAutoplayTests: async (userId: string): Promise<AutoplayTestResponse[]> => {
    return await api.get(`/autoplay-test/${userId}`);
  },

  /**
   * PUT /autoplay-test/:autoplayTestId/traffic
   * Atualiza a distribuição de tráfego do teste
   */
  updateTestTraffic: async (
    autoplayTestId: string,
    payload: UpdateAutoplayTestTrafficPayload
  ): Promise<AutoplayTestResponse> => {
    return await api.put(`/autoplay-test/${autoplayTestId}/traffic`, payload);
  },

  /**
   * Helper: Converte configuração local para payload do backend
   */
  convertConfigToPayload: (
    config: AutoplayABTestConfig,
    userId: string,
    title?: string
  ): CreateAutoplayTestPayload => {
    // Converter cada variação para o formato que o backend espera
    const autoplayTestConfig = (config.variations || []).map(variation => ({
      typesAutoplay: variation.templateId, // ID do template usado
      textSuperior: variation.customization.textTop,
      textInferior: variation.customization.textBottom,
      cor: variation.customization.colors.background,
      corText: variation.customization.colors.text,
      autoplayIconColor: variation.customization.colors.icon,
      haveBorderWhite: variation.customization.whiteBorder || false,
      havePulse: variation.customization.pulseAnimation || false,
      haveCustomImage: !!variation.customization.customImage,
      customImage: variation.customization.customImage || '',
      haveAutoplay: true, // Sempre true para testes de autoplay
    }));

    return {
      autoplayTestData: {
        autoplayTestTitle: title || 'Teste A/B de Autoplay',
        userId: userId,
        endDate: config.endDate || undefined,
      },
      autoplayTestConfig,
    };
  },

  /**
   * Helper: Converte resposta do backend para configuração local
   */
  convertResponseToConfig: (
    response: AutoplayTestResponse
  ): AutoplayABTestConfig => {
    return {
      enabled: response.status === 'active',
      variations: response.variations || [],
      trafficDistribution: response.trafficDistribution || {},
      variantTrafficDistribution: response.trafficDistribution || {},
      status: response.status,
      startDate: response.startDate,
      endDate: response.endDate,
      lastUpdated: response.updatedAt,
      isRealTimeEnabled: response.status === 'active',
    };
  },
};

export default autoplayTestService;
